<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('purchase.ordersFromSuppliers') }}
        </span>
        <h3 class="page-title">
          {{ $t('order.ordersList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end"
        style="gap: 0.75rem;">

        <d-button
          theme="primary"
          outline
          class="text-nowrap"
          @click.prevent.stop="loadFilteredData(true)">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm mr-1"
            role="status"></div>
          <template v-else>
            <i class="material-icons mr-1">cloud_download</i>
            {{ $t('order.downloadXls') }}
          </template>
        </d-button>

        <d-button
          theme="success"
          @click.prevent="$router.push('/zakupy/nowe')">
          <i class="material-icons">add</i>
          {{ $t('purchase.addOrder') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'purchases'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0"
              style="width: 50px;">ID</th>
            <th
              scope="col"
              class="border-0"
              style="width: 200px;">{{ $t('status.status') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 160px;">{{ $t('status.statusInSewingRoom') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.supplier') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px;">{{ $t('ui.orderName') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 120px;">{{ $t('ui.deadlineCap') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 160px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(purchase, index) of purchases"
            :key="'container-' + index">
            <td class="align-middle">
              {{ purchase.id }}
            </td>
            <td class="align-middle">
              <d-badge
                v-if="purchase.status === 'NEW'"
                theme="primary">
                {{ $t('purchase.newOrder') }}
              </d-badge>
              <d-badge
                v-if="purchase.status === 'WAITING_PAYMENT'"
                theme="warning">
                {{ $t('order.pendingPayment') }}
              </d-badge>
              <d-badge
                v-if="purchase.status === 'WAITING_PRODUCTS'"
                theme="warning">
                {{ $t('order.waitingPurchaseProducts') }}
              </d-badge>
              <d-badge
                v-if="purchase.status === 'CLOSED'"
                theme="danger">
                {{ $t('purchase.closed') }}
              </d-badge>
              <d-badge
                v-if="!purchase.status"
                theme="light">
                {{ $t('ui.lack') }}
              </d-badge>

              <d-badge
                v-if="purchase.products_received"
                theme="success"
                :outline="true"
                class="mt-1">
                {{ $t('purchase.allProductsReceived') }}
              </d-badge>

              <small
                v-if="purchase.exclude_from_erp_integration"
                class="text-danger">
                {{ $t('order.erp.isExcluded') }}
              </small>

              <d-badge
                v-if="!purchase.exclude_from_erp_integration"
                :theme="purchase.erp_optima_updated_at ? 'success' : 'danger'"
                class="w-100 mt-1"
                :outline="true">
                <template v-if="purchase.erp_optima_updated_at">
                  {{ $t('order.erp.integratedWithErp') }}
                </template>
                <template v-else>
                  {{ $t('order.erp.notIntegratedWithErp') }}
                </template>
              </d-badge>
            </td>
            <td class="align-middle">
              <div v-if="purchase.sewing_room_status">
                <small>
                  {{ $t('ui.supplier') }}:
                </small><br>
                <d-badge
                  :theme="sewingRoomStatus[purchase.sewing_room_status].style"
                  :outline="sewingRoomStatus[purchase.sewing_room_status].outline">
                  {{ sewingRoomStatus[purchase.sewing_room_status].text[prefferedLanguage] }}
                </d-badge>
              </div>

              <div v-if="purchase.sub_sewing_room_status">
                <small>
                  {{ $t('ui.subSupplier') }}:
                </small><br>
                <d-badge
                  :theme="sewingRoomStatus[purchase.sub_sewing_room_status].style"
                  :outline="sewingRoomStatus[purchase.sub_sewing_room_status].outline">
                  {{ sewingRoomStatus[purchase.sub_sewing_room_status].text[prefferedLanguage] }}
                </d-badge>
              </div>
            </td>
            <td class="align-middle">
              {{ purchase.customer_name }}
              <template v-if="purchase.sub_customer_name">
                <br>
                <small>
                  {{ $t('ui.subSupplier') }}:
                  <strong>{{ purchase.sub_customer_name }}</strong>
                </small>
              </template>

              <br v-if="purchase.received_at || purchase.suborder_received_at">

              <small v-if="purchase.received_at">
                {{ $t('purchase.receivedAt') }}: <strong class="text-dark">{{ purchase.received_at }}</strong>
              </small>

              <br v-if="purchase.received_at && purchase.suborder_received_at">

              <small v-if="purchase.suborder_received_at">
                {{ $t('purchase.suborderReceivedAt') }}: <strong class="text-dark">{{ purchase.suborder_received_at }}</strong>
              </small>
            </td>
            <td class="align-middle">
              <template v-if="purchase.orders_names">{{ purchase.orders_names }}</template>
              <template v-else>&mdash;</template>
              <br>
              <small>
                <strong>{{ $t('purchase.erpNumber') }}: </strong>
                <template v-if="purchase.erp_number">{{ purchase.erp_number }}</template>
                <template v-else>&mdash;</template>
              </small>
            </td>
            <td class="align-middle">
              {{ purchase.date }}
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zakupy/edycja/' + purchase.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="purchase.status !== 'CLOSED'"
                theme="danger"
                :disabled="removeInProgress"
                :title="$t('ui.delete')"
                @click.native="removePurchase(purchase.id, purchase.can_delete)">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="purchases.length === 0">
            <td
              colspan="9"
              class="text-center">
              {{ $t('purchase.noOrdersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';
import getPrefferedLanguage from './../utils/i18nUtils.js';
import sewingRoomStatuses from './../data/sewing-room-status.js';
import cloneDeep from 'lodash.clonedeep';
import Vue from 'vue';

export default {
  name: 'purchases',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCustomers;
    },
    customerOptions () {
      return this.customers.map(customer => ({
        text: customer.name,
        value: customer.id
      }));
    },
    purchaseStatusOptions () {
      return [
        {
          text: this.$t('purchase.newOrder'),
          value: 'NEW'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PRODUCTS'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'WAITING_PAYMENT'
        },
        {
          text: this.$t('purchase.closed'),
          value: 'CLOSED'
        }
      ];
    },
    sewingRoomStatusOptions () {
      let statuses = Object.keys(sewingRoomStatuses);
      let options = [];

      for (let i = 0; i < statuses.length; i++) {
        let status = statuses[i];
        options.push({
          text: sewingRoomStatuses[status].text[this.prefferedLanguage],
          value: status
        });
      }

      return options;
    },
    prefferedLanguage () {
      return getPrefferedLanguage();
    },
    sewingRoomStatus () {
      return sewingRoomStatuses;
    },
    userIsWorker () {
      return this.$store.getters.getUserType === 'worker';
    },
    filtersConfig () {
      return [
        {
          key: 'show_all',
          apiKey: 'show_all',
          label: this.$t('order.showAllOrders'),
          type: 'checkbox',
          isBasic: true,
          defaultValue: false,
          permission: () => this.userIsWorker
        },
        {
          key: 'order_name',
          apiKey: 'order_name',
          label: this.$t('ui.orderName'),
          placeholder: this.$t('purchase.enterOrderName'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.invoiceNumber'),
          placeholder: this.$t('purchase.enterInvoiceNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'customer',
          apiKey: 'customer_id',
          label: this.$t('shippers.shipperQuestionMark'),
          placeholder: this.$t('shippers.shipperQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.customerOptions
        },
        {
          key: 'status',
          apiKey: 'status',
          label: this.$t('status.statusQuestionMark'),
          placeholder: this.$t('status.statusQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.purchaseStatusOptions
        },
        {
          key: 'sewing_room_status',
          apiKey: 'sewing_room_status',
          label: this.$t('status.statusInSewingRoomQuestionMark'),
          placeholder: this.$t('status.statusInSewingRoomQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.sewingRoomStatusOptions
        }
      ];
    }
  },
  data () {
    return {
      purchases: [],
      customers: [],
      loaded: false,
      loadError: false,
      loadedCustomers: false,
      removeInProgress: false,
      downloadInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/items?where[is_supplier]=1',
        outputKey: 'customers',
        errorKey: 'loadCustomersError',
        loadedKey: 'loadedCustomers'
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData (downloadAsFile = false) {
      let where = cloneDeep(this.apiQueryPayload) || {};

      let requestShowAll = false;
      if (where.show_all) {
        requestShowAll = true;
        delete where.show_all;
      }

      if (downloadAsFile) {
        if (this.downloadInProgress) {
          return;
        }

        where['download_excel'] = 1;
        this.downloadInProgress = true;

        let requestConfig = {
          where
        };

        if (this.userIsWorker && requestShowAll) {
          requestConfig.show_all = 1;
        }

        this.$http.post('/purchases/items', requestConfig, {
          responseType: 'blob'
        }).then((response) => {
          let link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', this.$t('purchase.purchasesXlsFilename') + '.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.downloadInProgress = false;
        }).catch(() => {
          this.downloadInProgress = false;
        });
      } else {
        let requestConfig = {
          where
        };

        if (this.userIsWorker && requestShowAll) {
          requestConfig.show_all = 1;
        }

        ListUtils.loadPaginatedItemsData(this, {
          endpoint: '/purchases/items?page=' + this.currentPage,
          listField: 'purchases',
          params: requestConfig
        });
      }
    },
    removePurchase (purchaseID, canDelete) {
      if (!canDelete) {
        Vue.swal({
          title: this.$t('order.exisitngProductsInHplOrder'),
          html: this.$t('container.removeProductText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/purchases/delete/',
        id: purchaseID,
        successTitle: this.$t('order.orderDeleteSuccessTitle'),
        successText: this.$t('order.orderDeleteSuccessText'),
        confirmTitle: this.$t('order.orderDeleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderDeleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>
